import { Box, Dialog, DialogContent, useMediaQuery, useTheme } from '@mui/material';
import UploadReceiptHeader from './UploadReceiptHeader';
import UploadReceiptFormPanel from './UploadReceiptFormPanel';
import UploadReceiptPreviewPanel from './UploadReceiptPreviewPanel';
import UploadReceiptActions from './UploadReceiptActions';
import { useUploadReceiptDialogLogic } from '../../../hooks/logic/useUploadReceiptDialogLogic';
import type { UploadReceiptDialogProps } from '../../../types/uploadReceiptDialog';

const UploadReceiptDialog = ({
  open,
  onClose,
  onSuccess,
  stores: providedStores,
  storesLoading,
}: UploadReceiptDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    stores,
    selectedStore,
    locations,
    selectedLocation,
    locationsLoading,
    locationsResolved,
    selectedFile,
    previewUrl,
    loading,
    uploading,
    error,
    editMode,
    modeSwitching,
    perspectiveLoading,
    perspectiveKey,
    horizontalTilt,
    verticalTilt,
    rotation,
    cropperRef,
    perspectiveCropperRef,
    freeCropRef,
    fileInputRef,
    handleStoreChange,
    handleLocationChange,
    handleFileSelect,
    handleBrowseClick,
    handleDrop,
    handleDragOver,
    onCropperChange,
    onRotationChange,
    onRotationDecrease,
    onRotationIncrease,
    handleHorizontalTiltChange,
    onHorizontalTiltDecrease,
    onHorizontalTiltIncrease,
    handleVerticalTiltChange,
    onVerticalTiltDecrease,
    onVerticalTiltIncrease,
    handlePerspectiveState,
    useOriginalImage,
    startCornerCrop,
    startPerspectiveEdit,
    startRotateEdit,
    handleFreeCropRevert,
    handleUpload,
    handleClose,
    clearError,
  } = useUploadReceiptDialogLogic({ open, onClose, onSuccess, stores: providedStores, storesLoading });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: '16px' },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: '100dvh', sm: '90dvh' },
          maxHeight: { xs: '100dvh', sm: '90dvh' },
        },
      }}
    >
      <UploadReceiptHeader onClose={handleClose} />
      <DialogContent
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          scrollbarGutter: 'stable',
          p: "4px 20px"
        }}
      >
        <Box
          paddingY={2}
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            minHeight: 0,
            height: '100%',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 2,
          }}
        >
          <UploadReceiptFormPanel
            error={error}
            onClearError={clearError}
            stores={stores}
            selectedStore={selectedStore}
            locations={locations}
            selectedLocation={selectedLocation}
            locationsLoading={locationsLoading}
            locationsResolved={locationsResolved}
            loading={loading}
            onStoreChange={handleStoreChange}
            onLocationChange={handleLocationChange}
            selectedFile={selectedFile}
            fileInputRef={fileInputRef}
            onBrowseClick={handleBrowseClick}
            onFileSelect={handleFileSelect}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            editMode={editMode}
            modeSwitching={modeSwitching}
            onUseOriginal={useOriginalImage}
            onStartCornerCrop={startCornerCrop}
            onStartPerspectiveEdit={startPerspectiveEdit}
            onStartRotateEdit={startRotateEdit}
            onFreeCropRevert={handleFreeCropRevert}
            rotation={rotation}
            horizontalTilt={horizontalTilt}
            verticalTilt={verticalTilt}
            onRotationChange={onRotationChange}
            onRotationDecrease={onRotationDecrease}
            onRotationIncrease={onRotationIncrease}
            onHorizontalTiltChange={handleHorizontalTiltChange}
            onHorizontalTiltDecrease={onHorizontalTiltDecrease}
            onHorizontalTiltIncrease={onHorizontalTiltIncrease}
            onVerticalTiltChange={handleVerticalTiltChange}
            onVerticalTiltDecrease={onVerticalTiltDecrease}
            onVerticalTiltIncrease={onVerticalTiltIncrease}
          />
          <UploadReceiptPreviewPanel
            selectedFile={selectedFile}
            previewUrl={previewUrl}
            editMode={editMode}
            modeSwitching={modeSwitching}
            perspectiveLoading={perspectiveLoading}
            perspectiveKey={perspectiveKey}
            rotation={rotation}
            cropperRef={cropperRef}
            perspectiveCropperRef={perspectiveCropperRef}
            freeCropRef={freeCropRef}
            onCropperChange={onCropperChange}
            onPerspectiveState={handlePerspectiveState}
          />
        </Box>
      </DialogContent>
      <UploadReceiptActions
        uploading={uploading}
        modeSwitching={modeSwitching}
        perspectiveLoading={perspectiveLoading}
        selectedFile={selectedFile}
        selectedStore={selectedStore}
        locationRequired={locations.length > 1}
        selectedLocation={selectedLocation}
        locationsLoading={locationsLoading}
        locationsResolved={locationsResolved}
        onClose={handleClose}
        onUpload={handleUpload}
      />
    </Dialog>
  );
};

export default UploadReceiptDialog;
